import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';
import type { CustomField } from '@puckeditor/core';

import type { SelectOption } from '../types';

type SelectFieldProps = {
  field: { label?: string };
  name: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

/**
 * Returns a Puck custom field render function for enumerated CSS values.
 * The `options` parameter defines the available choices (e.g. font weights, text transforms).
 */
export function SelectField(options: readonly SelectOption[]) {
  return function SelectFieldWidget({ field, name, value, onChange, readOnly }: SelectFieldProps): JSX.Element {
    const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.currentTarget.value), [
      onChange,
    ]);

    // Saved page data is unvalidated JSON, so the stored value may not be one of the current options
    // -- content authored before an option existed, a renamed key, or a component that deliberately
    // accepts a raw CSS value alongside its presets (Section's max width). A <select> with no
    // matching <option> silently displays the *first* one, so the control would assert a value the
    // page is not actually using. Surfacing it keeps the panel honest, and leaves switching to a
    // preset an explicit choice rather than a side effect of opening the panel.
    const isUnlisted = value !== '' && value !== undefined && !options.some((opt) => opt.value === value);

    return (
      <FieldLabel label={field.label ?? name} readOnly={readOnly}>
        <select
          value={value}
          onChange={handleChange}
          disabled={readOnly}
          data-testid={`select-${name}`}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: '1px solid #d0d4d8',
            borderRadius: 4,
            fontSize: 14,
            background: '#fff',
          }}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
          {isUnlisted && <option value={value}>{`Custom (${value})`}</option>}
        </select>
      </FieldLabel>
    );
  };
}

/**
 * Builds a complete Puck `custom` select field, including the `ai.schema` the AI plugin
 * requires for every `custom` field.
 *
 * A bare `{ type: 'custom', render: SelectField(options) }` works fine in the editor UI
 * but 400s the *entire* AI chat request the moment the AI plugin reaches it ("Missing
 * schema for ... with custom field type") — this got missed repeatedly across CMS
 * component configs because nothing enforced it. Deriving the schema's enum from
 * `options` here means new call sites can't reproduce that bug by omission.
 */
export function selectField(
  label: string,
  options: readonly SelectOption[],
  aiInstructions?: string
): CustomField<string> {
  return {
    type: 'custom',
    label,
    render: SelectField(options),
    ai: {
      schema: { type: 'string', enum: options.map((option) => option.value) },
      ...(aiInstructions ? { instructions: aiInstructions } : {}),
    },
  };
}
